import { useFormSchema } from "@portal/providers/FormSchemaProvider";
import { type ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { Link, useSearchParams } from "react-router-dom";
import {
	getNestedValue,
	getPartialFieldPaths,
	getRequiredForApprovalFieldPaths,
	isEmpty,
} from "../../../utils/schema-fields";
import { cn } from "../../lib/utils";
import { PartialIndicator } from "../../primitives";
import { RequiredForApprovalIndicator } from "../../ui/RequiredForApprovalIndicator";
import { getNestedError } from "../utils/formResolver";
import { buildSummaryLink } from "../utils/summary.utils";

export type SummarySectionProps = {
	header: string;
	fieldPath?: string;
	to?: { pathname: string; section: string; id?: string };
	children: ReactNode;
	partial?: boolean;
};

export const SummarySection = ({
	header,
	fieldPath,
	to,
	children,
	partial: partialOverride,
}: SummarySectionProps) => {
	const schema = useFormSchema();
	const { getValues, formState } = useFormContext();
	const [searchParams] = useSearchParams();

	const isSubmitted = formState.isSubmitted;
	const values = getValues();

	const partial =
		partialOverride ??
		(() => {
			if (!fieldPath || !schema || !isSubmitted) return false;
			const partialPaths = getPartialFieldPaths(schema, values);
			return partialPaths.some(
				(path) =>
					(path.startsWith(`${fieldPath}.`) || path === fieldPath) &&
					isEmpty(getNestedValue(values, path)),
			);
		})();

	const requiredForApproval = (() => {
		if (!fieldPath || !schema || !isSubmitted) return false;
		const reqPaths = getRequiredForApprovalFieldPaths(schema, values);
		return reqPaths.some(
			(path) =>
				(path.startsWith(`${fieldPath}.`) || path === fieldPath) &&
				isEmpty(getNestedValue(values, path)),
		);
	})();

	const sectionError = fieldPath
		? getNestedError(formState.errors as Record<string, unknown>, fieldPath)
		: undefined;
	const hasError =
		isSubmitted &&
		!!sectionError &&
		typeof sectionError === "object" &&
		(sectionError as Record<string, unknown>).type === "error";

	return (
		<div className="flex flex-col space-y-[15px]">
			<div
				className={cn(
					"flex items-start space-x-[5px] text-[16px] text-tertiary font-semibold w-fit",
					hasError ? "text-red-500" : undefined,
				)}
			>
				{to !== undefined ? (
					<Link to={buildSummaryLink(to, searchParams)} className="cursor-pointer hover:underline">
						{header}
					</Link>
				) : (
					header
				)}
				{partial && <PartialIndicator active={true} className="mt-[4px]" />}
				{requiredForApproval && <RequiredForApprovalIndicator active={true} className="mt-[4px]" />}
			</div>
			<div className="w-full flex flex-col space-y-[15px] px-[10px]">{children}</div>
		</div>
	);
};
